import type { IntegrationStatus } from "../../lib/api";

interface IntegrationCardProps {
  name: string;
  description: string;
  icon: React.ReactNode;
  status: IntegrationStatus;
  lastSyncAt: string | null;
  errorMessage: string | null;
  isLoading: boolean;
  needsReconnect?: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  onSync?: () => void;
}

function formatLastSync(dateString: string | null): string {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
}

export function IntegrationCard({
  name,
  description,
  icon,
  status,
  lastSyncAt,
  errorMessage,
  isLoading,
  needsReconnect = false,
  onConnect,
  onDisconnect,
  onSync,
}: IntegrationCardProps) {
  const isConnected = status === "connected";
  const isError = status === "error";
  const showReconnectPrompt = isError && needsReconnect;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-gray-100">
            {icon}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{name}</h3>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isConnected && (
            <span className="flex items-center gap-1 text-sm text-green-600">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Connected
            </span>
          )}
          {isError && (
            <span className="flex items-center gap-1 text-sm text-red-600">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              Error
            </span>
          )}
        </div>
      </div>

      {isConnected && lastSyncAt && (
        <div className="mt-4 text-sm text-gray-500">
          Last synced: {formatLastSync(lastSyncAt)}
        </div>
      )}

      {isError && errorMessage && (
        <div className={`mt-4 p-3 rounded-lg text-sm ${
          showReconnectPrompt 
            ? "bg-amber-50 border border-amber-200 text-amber-800" 
            : "bg-red-50 border border-red-200 text-red-700"
        }`}>
          <div className="flex items-start gap-2">
            {showReconnectPrompt ? (
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            ) : (
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            )}
            <div>
              <p className="font-medium">
                {showReconnectPrompt ? "Session Expired" : "Connection Error"}
              </p>
              <p className="mt-1">{errorMessage}</p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 flex gap-2">
        {!isConnected ? (
          <button
            onClick={onConnect}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Connecting...
              </span>
            ) : isError ? (
              "Reconnect"
            ) : (
              "Connect"
            )}
          </button>
        ) : (
          <>
            {onSync && (
              <button
                onClick={onSync}
                disabled={isLoading}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Sync Now
              </button>
            )}
            <button
              onClick={onDisconnect}
              disabled={isLoading}
              className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? "Disconnecting..." : "Disconnect"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
